import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { analytics } from '../../services/analytics';
import SEOPageLayout from '../../components/SEOPageLayout';

const CateringPackages = () => {
  useEffect(() => {
    analytics.track('Template Page View', { template: 'Catering Quote Template' });
  }, []);

  return (
    <SEOPageLayout
      title="Free Catering Quote Template - Tiered Options (Good/Better/Best) | QuoteTier"
      description="Free catering quote template with Good/Better/Best menu tiers. Turn drop-off orders into buffet setups — and weddings into full-service events — with one quote structure."
      canonical="/templates/catering-packages-template"
      ogTitle="Free Catering Quote Template"
      ogDescription="Catering clients comparing three quotes default to the cheapest — unless yours already has three tiers built in. Template included, ready to send."
    >
      <section className="py-32 md:py-40 lg:py-48">
        <div className="container max-w-7xl mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="font-instrument text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight leading-tight mb-6">
              Free <span className="text-primary font-bold">Catering Quote Template</span>
            </h1>
            <p className="font-jetbrains text-xl md:text-2xl text-gray-600 tracking-tight leading-relaxed mb-8">
              Turn drop-off orders into buffet setups — and weddings into full-service events — by showing all three options on one page.
            </p>
            <div className="max-w-2xl mx-auto mb-10 p-5 border border-gray-200 rounded-lg bg-gray-50 text-left">
              <p className="font-jetbrains text-sm md:text-base text-gray-700 leading-relaxed">
                Catering is a three-quote comparison game — and the buyer who sees three flat rates usually picks the cheapest. The buyer who sees three tiers from <em>you</em> picks the middle one most of the time. The math is the same; the anchoring is completely different.
              </p>
            </div>
            <div className="font-instrument mt-8 flex flex-col sm:flex-row gap-6 justify-center items-center mb-3">
              <Link to="/signup">
                <Button className="px-8 py-4 text-xl font-semibold bg-black text-white hover:bg-gray-800 rounded-lg shadow-lg">
                  Use This Template Free
                </Button>
              </Link>
            </div>
            <p className="font-jetbrains text-sm text-gray-500 mb-6">Free account — no credit card required.</p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-4">Sample Tiers</h2>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            <Card className="border border-black">
              <CardHeader><CardTitle className="font-instrument text-xl">Drop-off</CardTitle><CardDescription>The Budget Option</CardDescription></CardHeader>
              <CardContent>
                  <ul className="font-jetbrains text-sm text-gray-600 list-disc list-inside">
                      <li>Delivery within 20km</li>
                      <li>Disposable serveware</li>
                      <li>1 protein + 2 sides</li>
                  </ul>
              </CardContent>
            </Card>
            <Card className="border border-black bg-black text-white">
              <CardHeader><CardTitle className="font-instrument text-xl text-white">Buffet Setup</CardTitle><CardDescription className="text-gray-300">Best Seller</CardDescription></CardHeader>
              <CardContent>
                  <ul className="font-jetbrains text-sm text-gray-300 list-disc list-inside">
                      <li>Everything in Drop-off</li>
                      <li>On-site setup & breakdown</li>
                      <li>Warming trays & chafing dishes</li>
                      <li>2 proteins + 4 sides</li>
                  </ul>
              </CardContent>
            </Card>
            <Card className="border border-black">
              <CardHeader><CardTitle className="font-instrument text-xl">Full Service</CardTitle><CardDescription>White Glove</CardDescription></CardHeader>
              <CardContent>
                  <ul className="font-jetbrains text-sm text-gray-600 list-disc list-inside">
                      <li>Everything in Buffet Setup</li>
                      <li>Professional serving staff</li>
                      <li>Bar & beverage service</li>
                      <li>Custom menu consultation</li>
                  </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-black text-white">
        <div className="container max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-6">Start Using This Template Now</h2>
          <Link to="/signup">
            <Button className="px-10 py-4 font-instrument text-xl font-semibold bg-white text-black hover:bg-gray-200 rounded-lg shadow-lg">
              Get Free Access
            </Button>
          </Link>
        </div>
      </section>
    </SEOPageLayout>
  );
};

export default CateringPackages;
